import { useCallback, useRef, useState } from "react";
import s from "./homework-card.module.scss";
import { IconDots, IconPencil, IconTrash } from "@tabler/icons-react";
import classNames from "classnames";
import { useAddHomeworkSubmission } from "@widgets/appointment/api/useAddHomeworkSubmission";
import { Button } from "@shared/ui/button/button";
import { AppImage, DEFAULT_AVATAR_URL } from "@shared/ui/app-image";
import { getFullName } from "@utils/lib/getFullname";
import { useClickOutside } from "@utils/lib/useClickOutside";
import { AppointmentContentBlock } from "../appointment-content-block/appointment-content-block";
import { CommentCard } from "../homework-comment-card/homework-comment-card";
import { TZDate } from "@date-fns/tz";
import { queryClient } from "@shared/api/query-client";
import { EditNote } from "../edit-note-card/edit-note-card";
import { useRemoveHomeworkModal } from "@features/appointment/remove-homework/model/use-remove-homework-modal";
import { SubmissionCard as TeacherSubmissionCard } from "../homework-submission-card/homework-submission-card";
import { MaterialComposer } from "../material-composer/material-composer";

const Submission = ({ homework, onSuccess, appointmentId }) => {
  const { mutateAsync: submit } = useAddHomeworkSubmission(appointmentId);

  const onClick = useCallback(
    ({ text, attachments, existingFileIds }) =>
      submit({
        appointmentId,
        homeworkId: homework.id,
        data: {
          text: text || null,
        },
        attachments,
        filesId: existingFileIds,
      }),
    [appointmentId, homework?.id, submit]
  );

  return (
    <MaterialComposer
      submitLabel="Отправить"
      startExpanded
      onSubmit={onClick}
      onCancel={onSuccess}
      showCollapseButton={false}
    />
  );
};

const StudentSubmissionCard = ({ submission, appointment }) => {
	const profileData = queryClient.getQueryData(["profile"])
	const profile = profileData?.data || profileData
	
	function formatDate(dateString) {
    const date = new TZDate(dateString, profile?.timezone);

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
	}

	return (
		<>
			<div className={s.contentBlock}>
				<div className={s.submissionHeader}>
					<div className={s.jcsb}>
						<span className={s.date}>{formatDate(submission.created_at)}</span>
						<span className={classNames(s.status, s[submission.status])}>
							{{
								'rejected': 'Отклонено',
								'approved': 'Принято',
								'created': 'Ожидает ответа',
							}[submission.status]}
						</span>
					</div>
					<div className={s.author}>
						<div className={s.avatar}>
							<AppImage
								src={
									appointment?.student?.avatar?.image
										? process.env.REACT_APP_STORAGE_URL + appointment?.student?.avatar.image
										: undefined
								}
								fallbackSrc={DEFAULT_AVATAR_URL}
								alt=""
							/>
						</div>
						<div className={s.content}>
							<h5 className={s.name}>
								{getFullName(appointment?.student)}
							</h5>
							<span className={s.role}>ученик</span>
						</div>
					</div>
				</div>
				
				<AppointmentContentBlock attachments={submission?.attachments} text={submission?.text}/>
			</div>
			{submission?.comment ? <CommentCard {...{submission, appointment}}/> : null}
		</>
	)
}

const isSameId = (a, b) => String(a) === String(b);

const formatCardDate = (dateString, timezone) => {
  if (!dateString) {
    return "";
  }

  const date = timezone ? new TZDate(dateString, timezone) : new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

export const HomeworkCard = ({
  homework,
  appointmentId,
  appointment,
  isTeacher,
  showAuthorInfo = true,
}) => {
  const profileData = queryClient.getQueryData(["profile"]);
  const profile = profileData?.data || profileData;

  const homeworkUserId = homework?.user_id || homework?.user?.id;
  const canManage = profile?.id && homeworkUserId && isSameId(profile.id, homeworkUserId);
  const { open: openRemoveModal } = useRemoveHomeworkModal();

  const [submissionIsActive, setSubmissionIsActive] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useClickOutside(menuRef, () => {
    if (isMenuOpen) {
      setTimeout(() => setIsMenuOpen(false), 50);
    }
  });

  const homeworkAuthor = appointment?.teacher
  const authorName = getFullName(homeworkAuthor).trim() || homeworkAuthor?.username || "Пользователь";
  const homeworkDate = formatCardDate(
    homework?.created_at || homework?.updated_at,
    profile?.timezone
  );

  if (isEditable && canManage) {
    return (
      <EditNote
        {...{ homework, appointmentId }}
        onSuccess={() => setIsEditable(false)}
      />
    );
  }

  return (
    <>
      <div className={s.contentBlock}>
        <div className={s.cardHeader}>
          {showAuthorInfo ? (
            <div className={s.materialAuthor}>
              <div className={s.materialAvatar}>
                <AppImage
                  src={
                    homeworkAuthor?.avatar?.image
                      ? process.env.REACT_APP_STORAGE_URL + homeworkAuthor.avatar.image
                      : undefined
                  }
                  fallbackSrc={DEFAULT_AVATAR_URL}
                  alt={authorName}
                />
              </div>
              <div className={s.materialAuthorInfo}>
                <div className={s.materialAuthorName}>{authorName}</div>
                {homeworkDate ? <div className={s.materialDate}>{homeworkDate}</div> : null}
              </div>
            </div>
          ) : (
            <div className={s.compactMeta}>{homeworkDate}</div>
          )}

          {canManage ? (
            <div className={s.menuWrapper} ref={menuRef}>
              <button
                className={s.menuButton}
                type="button"
                aria-label="Действия с домашним заданием"
                onClick={() => setIsMenuOpen((prev) => !prev)}
              >
                <IconDots size={20} />
              </button>

              {isMenuOpen ? (
                <div className={s.menu}>
                  <button
                    className={s.menuItem}
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsEditable(true);
                    }}
                  >
                    <IconPencil size={18} />
                    Редактировать
                  </button>
                  <button
                    className={`${s.menuItem} ${s.dangerItem}`}
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      openRemoveModal({ appointmentId, homeworkId: homework.id });
                    }}
                  >
                    <IconTrash size={18} />
                    Удалить материал
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <AppointmentContentBlock attachments={homework?.attachments} text={homework?.task} />
        {!isTeacher && !submissionIsActive ? (
          <Button
            label="Добавить результат"
            size="small"
            className={s.addButton}
            onClick={() => setSubmissionIsActive(true)}
          />
        ) : null}
      </div>
      {!isTeacher && submissionIsActive ? (
        <Submission {...{ homework, appointmentId }} onSuccess={() => setSubmissionIsActive(false)} />
      ) : null}
      {homework?.submissions?.map((submission) => (
        <div className={s.submissionItem} key={`submission-${submission?.id}`}>
          {isTeacher ? (
            <TeacherSubmissionCard {...{ submission, appointment, homework }} />
          ) : (
            <StudentSubmissionCard {...{ submission, appointment }} />
          )}
        </div>
      ))}
    </>
  );
};
